/**
 * Diálogo modal de la librería de UI (Radix `Dialog` + Tailwind).
 *
 * Radix aporta la accesibilidad y el comportamiento (foco atrapado, cierre con
 * Escape, `aria-modal`, vínculo de título/descripción, overlay que cierra al
 * pulsar fuera); aquí solo se ponen los estilos del proyecto. Sin lógica de
 * negocio (`docs/frontend_architecture.md`, Pr. 6).
 *
 * Se re-exporta el `Root`/`Trigger`/`Close` de Radix tal cual y se envuelven
 * `Overlay`/`Content` con estilos. El uso típico:
 *
 *     <Dialog>
 *       <DialogTrigger asChild><Button>Abrir</Button></DialogTrigger>
 *       <DialogContent>
 *         <DialogTitle>Título</DialogTitle>
 *         <DialogDescription>…</DialogDescription>
 *         …
 *       </DialogContent>
 *     </Dialog>
 */
import * as RadixDialog from "@radix-ui/react-dialog";
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactNode,
} from "react";

import { cn } from "./cn";

export const Dialog = RadixDialog.Root;
export const DialogTrigger = RadixDialog.Trigger;
export const DialogClose = RadixDialog.Close;
export const DialogTitle = forwardRef<
  ElementRef<typeof RadixDialog.Title>,
  ComponentPropsWithoutRef<typeof RadixDialog.Title>
>(function DialogTitle({ className, ...props }, ref) {
  return (
    <RadixDialog.Title
      ref={ref}
      className={cn("text-lg font-semibold text-slate-900", className)}
      {...props}
    />
  );
});

export const DialogDescription = forwardRef<
  ElementRef<typeof RadixDialog.Description>,
  ComponentPropsWithoutRef<typeof RadixDialog.Description>
>(function DialogDescription({ className, ...props }, ref) {
  return (
    <RadixDialog.Description
      ref={ref}
      className={cn("mt-1 text-sm text-slate-500", className)}
      {...props}
    />
  );
});

export interface DialogContentProps
  extends ComponentPropsWithoutRef<typeof RadixDialog.Content> {
  children: ReactNode;
}

/**
 * Contenido del modal: se porta en un `Portal` (fuera del flujo del DOM) con un
 * overlay oscuro y el panel centrado. Incluye un botón de cierre accesible.
 */
export const DialogContent = forwardRef<
  ElementRef<typeof RadixDialog.Content>,
  DialogContentProps
>(function DialogContent({ className, children, ...props }, ref) {
  return (
    <RadixDialog.Portal>
      <RadixDialog.Overlay className="fixed inset-0 z-40 bg-slate-900/40 data-[state=open]:animate-in" />
      <RadixDialog.Content
        ref={ref}
        className={cn(
          "fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-xl focus:outline-none",
          className,
        )}
        {...props}
      >
        {children}
        <RadixDialog.Close
          aria-label="Cerrar"
          className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
        >
          <span aria-hidden="true">×</span>
        </RadixDialog.Close>
      </RadixDialog.Content>
    </RadixDialog.Portal>
  );
});
